import { useMemo, useState } from 'react';
import { useChild } from '../../contexts/ChildContext';
import { useFirestore } from '../../hooks/useFirestore';
import DayTimeline from './DayTimeline';

/** YYYY-MM-DD in local time */
function dateKey(d) {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local date string from any ISO timestamp */
function localDate(iso) {
  if (!iso) return '';
  return dateKey(new Date(iso));
}

function filterByDate(items, target) {
  return items.filter(item => item.date && localDate(item.date) === target);
}

export default function Timeline() {
  const { selectedChild } = useChild();
  const childId = selectedChild?.id;

  const today = dateKey(new Date());
  const [selectedDate, setSelectedDate] = useState(today);

  const { items: sleepItems,  loading: l1 } = useFirestore('sleepTracking', childId);
  const { items: feedItems,   loading: l2 } = useFirestore('bottleFeeds',   childId);
  const { items: breastItems, loading: l3 } = useFirestore('breastfeeding', childId);
  const { items: diaperItems, loading: l4 } = useFirestore('diapers',       childId);
  const { items: waterItems,  loading: l5 } = useFirestore('waterIntake',   childId);
  const { items: solidItems,  loading: l6 } = useFirestore('solidFoods',    childId);

  const loading = l1 || l2 || l3 || l4 || l5 || l6;

  const dayData = useMemo(() => ({
    sleep:   filterByDate(sleepItems,  selectedDate),
    feeds:   filterByDate(feedItems,   selectedDate),
    breast:  filterByDate(breastItems, selectedDate),
    diapers: filterByDate(diaperItems, selectedDate),
    water:   filterByDate(waterItems,  selectedDate),
    solids:  filterByDate(solidItems,  selectedDate),
  }), [sleepItems, feedItems, breastItems, diaperItems, waterItems, solidItems, selectedDate]);

  const hasAnyData =
    dayData.sleep.length + dayData.feeds.length + dayData.breast.length +
    dayData.diapers.length + dayData.water.length + dayData.solids.length > 0;

  const goDay = (delta) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedDate(dateKey(d));
  };

  const isToday   = selectedDate === today;
  const isFuture  = selectedDate > today;

  const displayDate = new Date(`${selectedDate}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  if (!childId) {
    return <p className="text-muted">Select a child to view the timeline.</p>;
  }

  return (
    <div>
      {/* Header + date navigation */}
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <h2 className="mb-0" style={{ fontSize: '1.2rem', fontWeight: 700 }}>
          📅 Day Timeline
        </h2>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => goDay(-1)}
          >
            ‹ Prev
          </button>
          {!isToday && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setSelectedDate(today)}
            >
              Today
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => goDay(1)}
            disabled={isToday || isFuture}
          >
            Next ›
          </button>
        </div>
      </div>

      <p className="text-muted mb-3" style={{ fontSize: '0.88rem' }}>{displayDate}</p>

      {/* Legend */}
      <div
        className="d-flex flex-wrap gap-3 mb-3 pb-2"
        style={{
          fontSize: '0.72rem',
          color: 'var(--text-light)',
          borderBottom: '1px solid var(--border-light)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            display: 'inline-block', width: 12, height: 12,
            background: 'rgba(63,81,181,0.18)', border: '2px solid #3f51b5',
            borderRadius: 2,
          }} />
          Night sleep
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            display: 'inline-block', width: 12, height: 12,
            background: 'rgba(76,175,80,0.2)', border: '2px solid #4caf50',
            borderRadius: 2,
          }} />
          Nap
        </span>
        <span>🍼 Bottle</span>
        <span>🤱 Breastfeed</span>
        <span>🧷 Nappy</span>
        <span>💧 Water</span>
        <span>🥘 Solid food</span>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>Loading…</p>
        </div>
      ) : !hasAnyData ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📭</div>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            No entries logged for this day.
          </p>
        </div>
      ) : (
        <div
          style={{
            overflowY:    'auto',
            maxHeight:    '70vh',
            border:       '1px solid var(--border-light)',
            borderRadius: 8,
            padding:      '0.5rem 0.5rem 0.5rem 0',
            background:   'var(--bg-white)',
          }}
        >
          <DayTimeline
            sleepItems={dayData.sleep}
            feedItems={dayData.feeds}
            breastItems={dayData.breast}
            diaperItems={dayData.diapers}
            waterItems={dayData.water}
            solidItems={dayData.solids}
          />
        </div>
      )}
    </div>
  );
}
